// Upload-Seite: optional einen Mandant wählen, dann Drag&Drop / File-Picker.
// Die gewählte Kundennummer wird mit jedem Upload an die Cloud geschickt
// (C1 löst sie tenant-scoped auf, ADR-018/020). Ohne Auswahl landet der
// Beleg unzugeordnet und kann im Detail später zugeordnet werden.

import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useMandanten, useUpload } from "@/api/hooks";
import { Button } from "@/components/Button";
import { PageHeader } from "@/components/PageHeader";

import "./UploadPage.css";

interface FileResult {
  filename: string;
  ok: boolean;
  message: string;
  belegId?: string | null;
  duplicate?: boolean;
}

export function UploadPage() {
  const [dragOver, setDragOver] = useState(false);
  const [results, setResults] = useState<FileResult[]>([]);
  const [mandantKundennr, setMandantKundennr] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUpload();
  const mandanten = useMandanten(true);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const kundennr = mandantKundennr || undefined;
      Array.from(files).forEach((file) => {
        upload.mutate(
          { file, mandantKundennr: kundennr },
          {
            onSuccess: (res) =>
              setResults((prev) => [
                {
                  filename: file.name,
                  ok: true,
                  message: res.duplicate
                    ? "bereits vorhanden (Duplikat)"
                    : `${(res.file_size_bytes / 1024).toFixed(0)} KB · ${
                        res.mandant_id ? "Mandant zugeordnet" : "unzugeordnet"
                      }`,
                  belegId: res.id,
                  duplicate: res.duplicate,
                },
                ...prev,
              ]),
            onError: (err) =>
              setResults((prev) => [
                { filename: file.name, ok: false, message: err.message },
                ...prev,
              ]),
          }
        );
      });
    },
    [upload, mandantKundennr]
  );

  return (
    <>
      <PageHeader
        title="Beleg hochladen"
        subtitle="PDFs oder Bilder werden in die Cloud geladen und erscheinen im Dashboard."
      />

      <div className="bmd-card" style={{ marginBottom: 16 }}>
        <label className="bmd-label" htmlFor="upload-mandant">
          Mandant (optional)
        </label>
        <select
          id="upload-mandant"
          className="bmd-select"
          value={mandantKundennr}
          onChange={(e) => setMandantKundennr(e.target.value)}
          disabled={mandanten.isLoading}
        >
          <option value="">— ohne Zuordnung —</option>
          {(mandanten.data ?? []).map((m) => (
            <option key={m.id} value={m.bmd_kundennr}>
              {m.name} · {m.bmd_kundennr}
            </option>
          ))}
        </select>
        <div className="bmd-subtle" style={{ fontSize: 12, marginTop: 4 }}>
          Wird hier ein Mandant gewählt, ist der Beleg sofort zugeordnet —
          sonst lässt er sich im Detail nachträglich zuordnen.
        </div>
      </div>

      <div
        className={`bmd-upload-drop${
          dragOver ? " bmd-upload-drop-active" : ""
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            inputRef.current?.click();
          }
        }}
      >
        <div className="bmd-upload-mark" aria-hidden="true" />
        <div className="bmd-upload-title">
          Dateien hier ablegen oder klicken zum Auswählen
        </div>
        <div className="bmd-upload-sub">
          PDF, JPG, PNG, TIFF — max. 25&nbsp;MB pro Datei
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff"
          multiple
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = ""; // gleiche Datei erneut hochladbar
          }}
        />
        <div style={{ marginTop: 16 }}>
          <Button variant="primary" size="md">
            Datei auswählen
          </Button>
        </div>
      </div>

      {upload.isPending && (
        <div className="bmd-upload-status">Upload läuft…</div>
      )}

      {results.length > 0 && (
        <section className="bmd-card bmd-upload-results">
          <h3>Letzte Uploads</h3>
          <ul className="bmd-upload-list">
            {results.map((r, i) => (
              <li
                key={i}
                className={`bmd-upload-item ${
                  r.ok
                    ? r.duplicate
                      ? "bmd-upload-item-warn"
                      : "bmd-upload-item-ok"
                    : "bmd-upload-item-err"
                }`}
              >
                <div className="bmd-row bmd-row-between">
                  <div>
                    <strong>{r.filename}</strong>
                    <div className="bmd-subtle" style={{ fontSize: 13 }}>
                      {r.message}
                    </div>
                  </div>
                  {r.belegId != null && (
                    <Link
                      to={`/belege/${r.belegId}`}
                      className="bmd-mono"
                      style={{ fontWeight: 600 }}
                    >
                      öffnen →
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <p className="bmd-subtle" style={{ fontSize: 13 }}>
            Hochgeladene Belege erscheinen sofort im{" "}
            <Link to="/">Dashboard</Link>.
          </p>
        </section>
      )}
    </>
  );
}
